import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GuideCategories = ({ onSelectCategory }) => {
  const [activeFilter, setActiveFilter] = useState('all');

  const categories = [
    {
      id: 'home-size',
      title: 'By Home Size',
      description: 'Find the perfect vacuum power and capacity for your space',
      icon: 'Home',
      color: 'bg-blue-500',
      guides: [
        { name: 'Studio & 1-Bedroom Apartments', readers: '12.5K', updated: '2 days ago' },
        { name: '2-3 Bedroom Homes', readers: '18.2K', updated: '1 week ago' },
        { name: 'Large Homes (4+ Bedrooms)', readers: '9.8K', updated: '3 days ago' },
        { name: 'Multi-Story Houses', readers: '7.3K', updated: '5 days ago' }
      ],
      filter: 'popular'
    },
    {
      id: 'floor-type',
      title: 'By Floor Type',
      description: 'Specialized recommendations for different flooring materials',
      icon: 'Layers',
      color: 'bg-green-500',
      guides: [
        { name: 'Hardwood & Laminate Floors', readers: '15.7K', updated: '1 day ago' },
        { name: 'Deep Pile Carpets', readers: '11.4K', updated: '4 days ago' },
        { name: 'Area Rugs & Mixed Flooring', readers: '8.9K', updated: '6 days ago' },
        { name: 'Tile & Stone Surfaces', readers: '6.2K', updated: '1 week ago' }
      ],
      filter: 'trending'
    },
    {
      id: 'lifestyle',
      title: 'By Lifestyle Needs',
      description: 'Tailored solutions for your specific living situation',
      icon: 'Users',
      color: 'bg-purple-500',
      guides: [
        { name: 'Pet Owners Complete Guide', readers: '22.1K', updated: '12 hours ago' },
        { name: 'Allergy & Asthma Sufferers', readers: '14.6K', updated: '2 days ago' },
        { name: 'Busy Professionals', readers: '10.3K', updated: '3 days ago' },
        { name: 'Elderly & Mobility Considerations', readers: '5.8K', updated: '1 week ago' }
      ],
      filter: 'specialized'
    },
    {
      id: 'budget',
      title: 'By Budget Range',
      description: 'Maximum value recommendations for every price point',
      icon: 'DollarSign',
      color: 'bg-amber-500',
      guides: [
        { name: 'Best Under $100', readers: '19.4K', updated: '6 hours ago' },
        { name: 'Best Value $100-$300', readers: '16.8K', updated: '1 day ago' },
        { name: 'Premium $300-$600', readers: '12.1K', updated: '2 days ago' },
        { name: 'Luxury $600+', readers: '7.5K', updated: '4 days ago' }
      ],
      filter: 'budget'
    },
    {
      id: 'seasonal',
      title: 'Seasonal Guides',
      description: 'Timely recommendations for specific seasons and events',
      icon: 'Calendar',
      color: 'bg-red-500',
      guides: [
        { name: 'Spring Cleaning Essentials', readers: '13.2K', updated: '3 days ago' },
        { name: 'Holiday Gift Guide', readers: '8.7K', updated: '1 week ago' },
        { name: 'Back-to-School Apartment Setup', readers: '6.4K', updated: '2 weeks ago' },
        { name: 'Winter Deep Cleaning', readers: '4.9K', updated: '3 weeks ago' }
      ],
      filter: 'seasonal'
    },
    {
      id: 'technology',
      title: 'By Technology',
      description: 'Understanding different vacuum technologies and innovations',
      icon: 'Cpu',
      color: 'bg-indigo-500',
      guides: [
        { name: 'Robot Vacuums vs Traditional', readers: '17.3K', updated: '1 day ago' },
        { name: 'Cordless vs Corded Performance', readers: '14.9K', updated: '2 days ago' },
        { name: 'HEPA Filtration Explained', readers: '11.6K', updated: '5 days ago' },
        { name: 'Smart Features & Connectivity', readers: '8.1K', updated: '1 week ago' }
      ],
      filter: 'technical'
    }
  ];

  const filters = [
    { id: 'all', label: 'All Guides', icon: 'Grid3x3' },
    { id: 'popular', label: 'Most Popular', icon: 'TrendingUp' },
    { id: 'trending', label: 'Trending', icon: 'Flame' },
    { id: 'recent', label: 'Recently Updated', icon: 'Clock' }
  ];

  const filteredCategories = activeFilter === 'all' 
    ? categories 
    : categories?.filter(cat => cat?.filter === activeFilter);

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Complete Buying Guide Library
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto mb-8">
            Explore our comprehensive collection of expert-curated buying guides. Each guide is regularly updated with the latest products, testing data, and industry insights.
          </p>
          
          <div className="flex flex-wrap justify-center gap-3">
            {filters?.map((filter) => (
              <Button
                key={filter?.id}
                variant={activeFilter === filter?.id ? 'default' : 'outline'}
                size="sm"
                iconName={filter?.icon}
                iconPosition="left"
                onClick={() => setActiveFilter(filter?.id)}
              >
                {filter?.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {filteredCategories?.map((category) => (
            <div
              key={category?.id}
              className="bg-white border border-border rounded-xl shadow-subtle hover:shadow-floating transition-all duration-300 overflow-hidden"
            >
              <div className="p-6 border-b border-border">
                <div className="flex items-start space-x-4">
                  <div className={`${category?.color} p-3 rounded-lg flex-shrink-0`}>
                    <Icon name={category?.icon} size={24} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-text-primary mb-2">
                      {category?.title}
                    </h3>
                    <p className="text-text-secondary">
                      {category?.description}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  {category?.guides?.map((guide, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group"
                      onClick={() => onSelectCategory(category?.id, guide?.name)}
                    >
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary group-hover:text-primary transition-colors">
                          {guide?.name}
                        </h4>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-text-secondary">
                          <span className="flex items-center">
                            <Icon name="Users" size={12} className="mr-1" />
                            {guide?.readers} readers
                          </span>
                          <span className="flex items-center">
                            <Icon name="Clock" size={12} className="mr-1" />
                            Updated {guide?.updated}
                          </span>
                        </div>
                      </div>
                      <Icon name="ArrowRight" size={16} className="text-text-secondary group-hover:text-primary transition-colors" />
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 pt-6 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ExternalLink"
                    iconPosition="right"
                    fullWidth
                    onClick={() => onSelectCategory(category?.id)}
                  >
                    View All {category?.title} Guides
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GuideCategories;